import { fetchEventSource } from '@microsoft/fetch-event-source';
import { CustomUserClaims } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useReducer,
    useRef,
    useState,
} from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AutomateError, sync, test } from '../api';
import { getAccessToken } from '../okta';
import { initializePendo } from '../pendo';
import { getConnectors, getConnectorsHash, setConnectors, setConnectorsHash } from '../storage';
import {
    ActivationStat,
    AlertPayload,
    AlertPopup,
    AutomationTask,
    AutomationTaskEdit,
    AutomationTaskStatus,
    Connector,
    Notification,
    SyncResponse,
} from '../tasks/types';
import { translations } from '../translations/translations';
import { newGuid, stringReplace, tryGetErrorMessage } from './utils';

declare global {
    interface Window {
        getFlags: () => Record<string, boolean>;
        getFlag: (name: string) => boolean;
        setFlag: (name: string, value: boolean) => void;
    }
}

export interface AppContext {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    tasks: AutomationTask[];
    filteredTasks: AutomationTask[];
    setFilteredTasks: (tasks: AutomationTask[]) => void;
    connectors: Record<string, Connector>;
    language: string;
    setLanguage: (language: string) => void;
    onUpdateConnectorScope: (connectorId: string, scopes: string[]) => void;
    notificationMessage: string;
    isOnline: boolean;
    userInfo: CustomUserClaims | null;
    notifications: Array<Notification>;
    setNotifications: (notifications: Array<Notification>) => void;
    hasUnreadNotifications: boolean;
    setHasUnreadNotifications: (hasUnreadNotifications: boolean) => void;
    activationStats: Array<ActivationStat>;
    setActivationStats: (activationStats: Array<ActivationStat>) => void;
    getActivations: (task: AutomationTask) => number;
    currentUserProfileImage: string | null;
    setCurrentUserProfileImage: (profileImage: string | null) => void;
    onUpdateConnectorNonRedirectAuth: (connectorId: string, authorized: boolean) => void;
    flags: Record<string, boolean>;
    syncAndUpdate: (
        tasksToSync?: AutomationTaskEdit[],
        updatedUserProfileImage?: string | null,
        sendHash?: boolean,
    ) => Promise<SyncResponse | void>;
    alerts: AlertPopup[];
    removeAlert: (alertId: AlertPopup['id']) => void;
    alertSuccess: (payload: Omit<AlertPayload, 'type'>) => void;
    alertError: (payload: Omit<AlertPayload, 'type'>) => void;
    testTask: (task: AutomationTaskEdit, outputs: Record<string, string>) => Promise<void>;
    listenForAuthenticationEvent: (connectorId: string) => Promise<void>;
}

type TaskAction =
    | { type: 'setTask'; task: AutomationTask }
    | { type: 'setAllTasks'; tasks: AutomationTask[] };

type ConnectorAction =
    | { type: 'setConnector'; connector: Connector }
    | { type: 'setAllConnectors'; connectors: Record<string, Connector> };

const AppContext = createContext<AppContext | undefined>(undefined);

const tasksReducer = (tasks: AutomationTask[], action: TaskAction) => {
    switch (action.type) {
        case 'setTask':
            return tasks.map((t) => (t.id === action.task.id ? action.task : t));
        case 'setAllTasks':
            return action.tasks;
    }
};

const connectorsReducer = (connectors: Record<string, Connector>, action: ConnectorAction) => {
    switch (action.type) {
        case 'setConnector':
            return { ...connectors, [action.connector.id]: action.connector };
        case 'setAllConnectors':
            return action.connectors;
    }
};

const AppProvider = ({
    children,
    isTesting = false,
}: {
    children?: ReactNode;
    isTesting?: boolean;
}) => {
    const navigate = useNavigate();
    const { oktaAuth, authState } = useOktaAuth();

    // Start off with loading set to true to prevent the "You have no tasks..." message
    // from flashing in and out even if we do have some tasks.
    const [isLoading, setIsLoading] = useState(true);
    const [tasks, dispatchTasks] = useReducer(tasksReducer, []);
    const [filteredTasks, setFilteredTasks] = useState<AutomationTask[]>([]);
    const [connectors, dispatchConnectors] = useReducer(connectorsReducer, getConnectors());
    const [userInfo, setUserInfo] = useState<CustomUserClaims | null>(null);
    const [language, setLanguage] = useState('en');
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [activationStats, setActivationStats] = useState<Array<ActivationStat>>([]);
    const [currentUserProfileImage, setCurrentUserProfileImage] = useState<string | null>(null);
    const [flags, setFlags] = useState<Record<string, boolean>>({
        boomiAI: false,
    });
    const [alerts, setAlerts] = useState<AlertPopup[]>([]);
    const [abortController, setAbortController] = useState(new AbortController());
    const isInitializedRef = useRef(false);

    // While it is possible to spoof onLine with software, for now we are assuming the average layman using Task isn't doing anything like this.
    // A better solution using webhooks or long polling is an option if we find this is throwing too many false positives.
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    // This is a single string for now but we'll probably want some kind of notification queue system later on to handle multiple errors arriving at once.
    const [notificationMessage, setNotificationMessage] = useState('');

    // Only for dev use, react code reads using flags context value
    window.getFlags = () => flags;
    window.getFlag = (name: string) => flags[name] ?? 'Flag not found';
    window.setFlag = (name: string, value: boolean) =>
        setFlags({
            ...flags,
            [name]: value,
        });

    const removeAlert = (alertId: AlertPopup['id']) => {
        // Mark the alert as removed; this will trigger the fade-out animation
        // Use an updater function to ensure concurrent updates do not override each other
        setAlerts((existingAlerts) => {
            const updatedAlerts = existingAlerts.map((alert) => {
                if (alert.id === alertId) {
                    return { ...alert, isRemoved: true };
                }

                return alert;
            });

            return updatedAlerts;
        });

        // Actually remove the the alert from the DOM when the fade out transition completes
        document.getElementById(`alert-${alertId}`)?.addEventListener('transitionend', (event) => {
            // We care only about the opacity transition
            if (event.propertyName !== 'opacity') {
                return;
            }

            setAlerts((existingAlerts) => {
                const updatedAlerts = existingAlerts.filter((alert) => alert.id !== alertId);
                return updatedAlerts;
            });
        });
    };

    const addAlert = useCallback((alert: Omit<AlertPopup, 'id'>) => {
        const newAlert: AlertPopup = {
            ...alert,
            id: newGuid(),
        };

        // Remove non-persistent alerts after five seconds
        if (!newAlert.isPersistent) {
            window.setTimeout(() => removeAlert(newAlert.id), 5_000);
        }

        // Use an updater function to ensure concurrent updates do not override each other
        setAlerts((existingAlerts) => {
            const alertsToKeep = existingAlerts
                // Alerts marked as removed don't count towards this as they'll shortly disappear anyway
                .filter((alert) => !alert.isRemoved)
                // Allow at most four alerts on screen at once
                .slice(-3);

            const updatedAlerts = [...alertsToKeep, newAlert];

            return updatedAlerts;
        });
    }, []);

    const alertSuccess = ({
        message,
        traceId,
        isPersistent = false,
    }: Omit<AlertPayload, 'type'>) => {
        addAlert({ message, traceId, isPersistent, type: 'success' });
    };

    const alertError = useCallback(
        ({ message, traceId, isPersistent = true }: Omit<AlertPayload, 'type'>) => {
            addAlert({ message, traceId, isPersistent, type: 'error' });
        },
        [addAlert],
    );

    const syncAndUpdate = useCallback(
        async (
            tasksToSync: AutomationTaskEdit[] = [],
            updatedUserProfileImage: string | null = null,
            sendHash = true,
        ) => {
            setIsLoading(true);

            try {
                const response = await sync(
                    tasksToSync,
                    sendHash && Object.keys(connectors).length > 0 ? getConnectorsHash() : null,
                    updatedUserProfileImage ?? null,
                    // We should get the user image if:
                    // - we have just updated the user image
                    // - we don't have any user image
                    // - we already have a user image, but we have just updated the user image
                    !!updatedUserProfileImage || !currentUserProfileImage,
                );

                if (response.tasks) {
                    dispatchTasks({ type: 'setAllTasks', tasks: response.tasks });
                }

                setConnectorsHash(response.hash);

                if (response.definitions?.connectors) {
                    dispatchConnectors({
                        type: 'setAllConnectors',
                        connectors: response.definitions.connectors,
                    });

                    setConnectors(response.definitions.connectors);
                }

                if (!!updatedUserProfileImage || !currentUserProfileImage) {
                    setCurrentUserProfileImage(response.profileImageDataBase64);
                }

                setNotifications(response.notifications);

                return response;
            } catch (error) {
                if (!(error instanceof AutomateError)) {
                    return;
                }

                const taskName = tasksToSync?.length === 1 ? tasksToSync[0].displayName : null;
                const isDelete =
                    tasksToSync?.length === 1 &&
                    tasksToSync[0].status === AutomationTaskStatus.Deleted;
                const errorMessage = taskName
                    ? isDelete
                        ? stringReplace(translations[language].ERROR_deleting_task, taskName)
                        : stringReplace(translations[language].ERROR_saving_one_task, taskName)
                    : tasksToSync?.length > 0
                      ? translations[language].ERROR_saving_multiple_tasks
                      : updatedUserProfileImage !== null
                        ? translations[language].ERROR_updating_profile_picture
                        : translations[language].ERROR_getting_tasks;
                alertError({ message: errorMessage, traceId: error.json.traceId });
            } finally {
                // After the loading is done we still need to give the reducer a bit of time
                // to do his thing before setting isLoading to false and rendering the task list.
                // If not then we'll render the list with no tasks, then re-render it again
                // once the reducer is done which will cause the "You have no tasks..." message
                // flash in and out even if we do have some tasks.
                requestAnimationFrame(() => setIsLoading(false));
            }
        },
        [alertError, connectors, currentUserProfileImage, language],
    );

    const initialSync = useCallback(
        async (tasksToSync?: AutomationTask[]) => {
            try {
                if (!authState?.accessToken?.accessToken) {
                    return;
                }

                // No need to sync tasks that haven't been changed since the last sync.
                const filteredTasksToSync = (tasksToSync ?? tasks).filter(
                    ({ status }) => status !== AutomationTaskStatus.Unchanged,
                );

                const [response, userInfo] = await Promise.all([
                    syncAndUpdate(filteredTasksToSync),
                    oktaAuth.token.getUserInfo(authState.accessToken, authState.idToken),
                ]);

                setUserInfo(userInfo);

                if (response && response.user) {
                    initializePendo({
                        id: response.user.id,
                        email: response.user.email,
                        full_name: `${userInfo.given_name} ${userInfo.family_name}`,
                    });
                }

                return response;
            } catch (error) {
                alertError({
                    message: tryGetErrorMessage(error, language),
                });
                console.error(error);
            }
        },
        [
            alertError,
            authState?.accessToken,
            authState?.idToken,
            language,
            oktaAuth.token,
            syncAndUpdate,
            tasks,
        ],
    );

    useEffect(() => {
        // Initialization should occur at most once
        // Use a ref as state updates don't occur before second effect run in dev mode
        if (isInitializedRef.current) {
            return;
        } else {
            isInitializedRef.current = true;
        }

        const load = async () => {
            const syncResponse = await initialSync();

            if (
                (location.pathname.includes('/tasks/new-task') === false &&
                    location.pathname.includes('/tasks')) ||
                ['', '/', undefined].includes(location.pathname)
            ) {
                const paramId = location.pathname.split('/tasks/')[1];

                if (syncResponse?.tasks?.length && syncResponse.tasks.length > 0) {
                    if (syncResponse.tasks.every(({ id }) => id !== paramId)) {
                        // If you have tasks, show them
                        navigate('/tasks');
                    }
                } else {
                    // If you don't have tasks, make your first one
                    navigate('/tasks/new');
                }
            }
        };

        const onConnectionChange = (connected: boolean) => {
            setIsOnline(connected);
        };

        window.addEventListener('online', () => onConnectionChange(true));
        window.addEventListener('offline', () => onConnectionChange(false));

        void load();

        return () => {
            window.removeEventListener('online', () => onConnectionChange(true));
            window.removeEventListener('offline', () => onConnectionChange(false));
        };
    }, [initialSync, navigate]);

    useEffect(() => {
        if (isOnline) {
            setNotificationMessage('');
        } else {
            setNotificationMessage(translations[language].COMMON_offline_message);
        }
    }, [isOnline, language]);

    const closeEventSource = () => {
        abortController.abort();
        setAbortController(new AbortController());
    };

    const listenForAuthenticationEvent = async (connectorId: string) => {
        const oktaToken = await getAccessToken();
        const { signal } = abortController;
        const fetchAuthEvents = async () => {
            await fetchEventSource(`/api/event/authentication/${connectorId}`, {
                method: 'GET',
                headers: {
                    Accept: 'text/event-stream',
                    Authorization: `Bearer ${oktaToken?.accessToken}`,
                },
                async onopen(res): Promise<void> {
                    if (res.ok && res.status === 200) {
                        console.log('Connection made ', res);
                    } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
                        console.log('Client side error ', res);
                    }
                },
                async onmessage(event) {
                    if (event.event === 'authentication') {
                        //We're only listening for one successful event
                        console.log('Authentication event recieved');
                        closeEventSource();
                        await initialSync();
                    }
                },
                onclose() {
                    console.log('Connection closed by the server');
                    closeEventSource();
                },
                onerror(err) {
                    console.log('There was an error from server', err);
                    closeEventSource();
                },
                signal,
            });
        };

        if (oktaToken?.accessToken) {
            fetchAuthEvents();
        }
    };

    const onUpdateConnectorScope = (connectorId: string, scopes: string[]) => {
        dispatchConnectors({
            type: 'setConnector',
            connector: {
                ...connectors[connectorId],
                authorizedScopes: [...(connectors[connectorId].authorizedScopes ?? []), ...scopes],
            },
        });
    };

    const onUpdateConnectorNonRedirectAuth = (connectorId: string, authorized: boolean) => {
        dispatchConnectors({
            type: 'setConnector',
            connector: {
                ...connectors[connectorId],
                authorized,
            },
        });
    };

    const onDeleteConnectorScope = (connectorId: string) => {
        dispatchConnectors({
            type: 'setConnector',
            connector: {
                ...connectors[connectorId],
                authorizedScopes: [],
            },
        });
    };

    const getActivations = (task: AutomationTask) => {
        const eventActivations =
            activationStats.find((value) => {
                return value.taskId === task.id;
            })?.activations ?? 0;

        return Math.max(task.activations, eventActivations);
    };

    const testTask = (task: AutomationTaskEdit, outputs: Record<string, string>) =>
        test(task, outputs);

    const contextValue = {
        isLoading,
        setIsLoading,
        tasks,
        connectors,
        language,
        setLanguage,
        onUpdateConnectorScope,
        onDeleteConnectorScope,
        notificationMessage,
        isOnline,
        userInfo,
        filteredTasks,
        setFilteredTasks,
        notifications,
        setNotifications,
        hasUnreadNotifications,
        setHasUnreadNotifications,
        activationStats,
        setActivationStats,
        getActivations,
        currentUserProfileImage,
        setCurrentUserProfileImage,
        onUpdateConnectorNonRedirectAuth,
        flags,
        syncAndUpdate,
        alerts,
        removeAlert,
        alertSuccess,
        alertError,
        testTask,
        listenForAuthenticationEvent,
    };

    // For testing purposes we need the ability to pass in children, normally <Outlet /> will be used.
    const providerChildren = isTesting ? children : <Outlet />;

    return <AppContext.Provider value={contextValue}>{providerChildren}</AppContext.Provider>;
};

const useApp = (): AppContext => {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useApp must be used within a AppProvider');
    }
    return context;
};

export { AppProvider, useApp };
